import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  TrendingUp,
  Award,
  Flame,
  Zap,
  Trophy,
  Camera,
  MessageSquare,
  Bell,
} from 'lucide-react';
import { LevelBadge } from '../components/LevelBadge';
import axios from 'axios';
import { API, avatarUrl } from '../utils/api';
import { getLevelTitle, XP_PER_LEVEL } from '../utils/levelSystem';

export const Dashboard = ({ user, onUserUpdate }) => {
  const navigate = useNavigate();
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [unreadFeedbacks, setUnreadFeedbacks] = useState(0);
  const [goalData, setGoalData] = useState(null);
  const levelInfo = getLevelTitle(user?.level || 1);

  useEffect(() => {
    const DASH_CACHE = 'ferraz_dashboard_cache';

    const fetchData = async () => {
      try {
        const [userRes, workoutsRes, feedbacksRes, messagesRes, goalRes] = await Promise.all([
          axios.get(`${API}/users/me`),
          axios.get(`${API}/workouts`),
          axios.get(`${API}/user/feedbacks/unread/count`).catch(() => ({ data: { unread_count: 0 } })),
          axios.get(`${API}/user/messages/unread/count`).catch(() => ({ data: { unread_count: 0 } })),
          axios.get(`${API}/goal-progress`).catch(() => ({ data: null })),
        ]);
        onUserUpdate(userRes.data);
        const workouts = workoutsRes.data.slice(0, 3);
        const unread = (feedbacksRes.data.unread_count || 0) + (messagesRes.data.unread_count || 0);
        setRecentWorkouts(workouts);
        setUnreadFeedbacks(unread);
        if (goalRes.data) setGoalData(goalRes.data);
        // Cache for offline
        try {
          localStorage.setItem(DASH_CACHE, JSON.stringify({ workouts, unread, goalData: goalRes.data }));
        } catch (e) { console.warn('[Dashboard] non-critical failure', e); }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Load from cache on failure
        try {
          const cached = JSON.parse(localStorage.getItem(DASH_CACHE));
          if (cached) {
            setRecentWorkouts(cached.workouts || []);
            setUnreadFeedbacks(cached.unread || 0);
            if (cached.goalData) setGoalData(cached.goalData);
          }
        } catch (e) { console.warn('[Dashboard] non-critical failure', e); }
      }
    };
    fetchData();

    // Refetch when app becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [onUserUpdate]);

  const goalWorkouts = goalData?.workouts_done ?? (user?.workouts_this_month || 0);
  const goalTarget = goalData?.monthly_goal ?? (user?.monthly_goal || 4);
  const monthProgress = goalTarget > 0 ? (goalWorkouts / goalTarget) * 100 : 0;
  const hasPlanPeriod = goalData?.period_start && goalData?.period_end;

  const getMotivationalMessage = () => {
    const progress = user?.current_xp || 0;
    const total = 500;
    const percentage = (progress / total) * 100;

    if (percentage >= 80) return 'Quase lá! Você está arrasando!';
    if (percentage >= 50) return 'Você está no caminho certo!';
    if (user?.streak >= 7) return `${user.streak} dias seguidos! Impressionante!`;
    if (goalWorkouts >= goalTarget && goalTarget > 0)
      return 'Meta concluída! Parabéns!';
    return 'Continue assim! Você está evoluindo!';
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24" data-testid="dashboard">
      <div className="px-6 pt-2 pb-6">
        <motion.div
          className="flex items-center justify-between mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4">
            <div className="relative">
              {user?.avatar_v ? (
                <img
                  src={avatarUrl(user.id, user.avatar_v)}
                  alt={user?.name}
                  className="w-16 h-16 rounded-full border-2 border-neon-lime"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neon-lime to-utility-success flex items-center justify-center text-black font-black text-2xl">
                  {user?.name?.[0]}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-neon-lime rounded-full flex items-center justify-center text-black font-black text-xs border-2 border-[#050505]">
                {user?.level || 1}
              </div>
            </div>
            <div>
              <h1 className="font-primary font-black text-3xl text-white uppercase tracking-tighter">
                Olá, {user?.name}!
              </h1>
              <div className="flex flex-col mt-1">
                <div className="flex items-center gap-2">
                  <span className="text-neon-lime font-bold text-sm">{levelInfo.name}</span>
                  <span className="text-zinc-600">•</span>
                  <span className="text-zinc-400 text-xs">{levelInfo.description}</span>
                </div>
                <span className="text-zinc-500 text-sm font-secondary mt-0.5">
                  {getMotivationalMessage()}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-neon-lime" />
              <span className="text-zinc-400 text-sm font-medium uppercase tracking-wider">
                Pontos
              </span>
            </div>
            <div className="text-right">
              <span className="font-primary font-black text-2xl text-neon-lime">
                {Math.round(user?.total_xp || 0)}
              </span>
            </div>
          </div>
          <div className="relative h-4 bg-zinc-900 rounded-full overflow-hidden border border-zinc-800">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-neon-lime via-utility-success to-neon-lime"
              style={{
                width: `${((user?.current_xp || 0) / XP_PER_LEVEL) * 100}%`,
                boxShadow: '0 0 20px rgba(204, 255, 0, 0.6)',
              }}
              initial={{ width: 0 }}
              animate={{
                width: `${((user?.current_xp || 0) / XP_PER_LEVEL) * 100}%`,
              }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-white drop-shadow-lg z-10">
                {Math.round(user?.current_xp || 0)} / {XP_PER_LEVEL} para Nível {(user?.level || 1) + 1}
              </span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <motion.div
            className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-5 border border-white/5 relative overflow-hidden"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02 }}
            data-testid="monthly-goal-card"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-neon-purple/10 rounded-full blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-neon-purple" />
                <span className="text-zinc-400 text-xs uppercase tracking-wider font-bold">
                  Meta do Treino
                </span>
              </div>
              <div className="mb-3">
                <div className="font-primary font-black text-4xl text-white mb-1">
                  {goalWorkouts}
                  <span className="text-zinc-600 text-2xl">/{goalTarget}</span>
                </div>
                <div className="text-xs text-zinc-500">
                  {hasPlanPeriod 
                    ? `${new Date(goalData.period_start + 'T00:00:00').toLocaleDateString('pt-BR', {day:'2-digit',month:'2-digit'})} - ${new Date(goalData.period_end + 'T00:00:00').toLocaleDateString('pt-BR', {day:'2-digit',month:'2-digit'})}`
                    : 'treinos'
                  }
                </div>
              </div>
              <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-neon-purple to-neon-pink"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(monthProgress, 100)}%` }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-5 border border-white/5 relative overflow-hidden"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            whileHover={{ scale: 1.02 }}
            data-testid="streak-card"
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">👏</span>
                <span className="text-zinc-400 text-xs uppercase tracking-wider font-bold">
                  Sequência
                </span>
              </div>
              <div className="mb-3">
                <div className="font-primary font-black text-4xl text-orange-500 mb-1">
                  {user?.streak || 0}
                </div>
                <div className="text-xs text-zinc-500">dias seguidos</div>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(7)].map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded-full ${
                      i < (user?.streak || 0) % 7
                        ? 'bg-orange-500'
                        : 'bg-zinc-800'
                    }`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <motion.div
            className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-5 border border-white/5"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            data-testid="level-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-neon-lime" />
              <span className="text-zinc-400 text-xs uppercase tracking-wider font-bold">
                Nível
              </span>
            </div>
            <div className="font-primary font-black text-4xl text-white">
              {user?.level || 1}
            </div>
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-5 border border-white/5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            data-testid="coins-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <img src="/fercoin.png" alt="FerCoin" className="w-10 h-10" />
              <span className="text-zinc-400 text-xs uppercase tracking-wider font-bold">
                FerCoin
              </span>
            </div>
            <div className="font-primary font-black text-4xl text-yellow-400">
              {user?.coins || 0}
            </div>
          </motion.div>
        </div>

        {/* Feedbacks Card */}
        <motion.div
          onClick={() => navigate('/my-feedbacks')}
          className={`bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-4 border cursor-pointer hover:bg-zinc-800/50 transition-all mb-6 ${
            unreadFeedbacks > 0 ? 'border-neon-purple/50' : 'border-white/5'
          }`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                unreadFeedbacks > 0 ? 'bg-neon-purple/20' : 'bg-zinc-800'
              }`}>
                <MessageSquare className={`w-5 h-5 ${unreadFeedbacks > 0 ? 'text-neon-purple' : 'text-zinc-400'}`} />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">Meus Feedbacks</h3>
                <p className="text-zinc-500 text-xs">Conversas com seu treinador</p>
              </div>
            </div>
            {unreadFeedbacks > 0 && (
              <div className="flex items-center gap-1 bg-neon-purple/20 text-neon-purple px-3 py-1 rounded-full">
                <Bell className="w-4 h-4" />
                <span className="text-sm font-bold">{unreadFeedbacks}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Ranking Quick Link */}
        <motion.div
          onClick={() => navigate('/ranking')}
          className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-4 border border-white/5 cursor-pointer hover:bg-zinc-800/50 transition-all mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.47 }}
          data-testid="ranking-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h3 className="font-bold text-white text-sm">Ranking</h3>
                <p className="text-zinc-500 text-xs">Veja sua posição no Top 10</p>
              </div>
            </div>
            <span className="text-zinc-600 text-xs">Ver &rarr;</span>
          </div>
        </motion.div>

        {recentWorkouts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h2 className="font-primary font-bold text-xl text-white uppercase mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-neon-lime" />
              Últimos Treinos
            </h2>
            <div className="space-y-3">
              {recentWorkouts.map((workout, index) => (
                <motion.div
                  key={workout.id}
                  className="bg-zinc-900/50 border border-white/5 rounded-xl p-4 flex items-center justify-between"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                >
                  <div className="flex items-center gap-3">
                    {workout.has_photo && (
                      <Camera className="w-5 h-5 text-utility-success" />
                    )}
                    <div>
                      <div className="text-sm text-zinc-300">
                        {new Date(workout.date).toLocaleDateString('pt-BR')}
                      </div>
                      {workout.notes && (
                        <div className="text-xs text-zinc-500 mt-1">
                          {workout.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-neon-lime font-bold">
                    <Zap className="w-4 h-4" />
                    +{workout.xp_earned}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};
